import { Phone, ShieldCheck, Zap, MapPin, PlayCircle } from "lucide-react";
import { PHONE, HERO_VIDEO_URL, HERO_POSTER } from "@/lib/siteConfig";

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden ss-hero-bg text-white"
    >
      <div className="absolute inset-0 ss-grain pointer-events-none" aria-hidden="true" />

      <div className="ss-container relative pt-14 pb-20 sm:pt-20 sm:pb-28 lg:pt-24 lg:pb-32">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left: copy */}
          <div>
            <div className="ss-eyebrow-on-navy" data-testid="hero-eyebrow">
              <ShieldCheck className="h-3.5 w-3.5" />
              Deutschlandweit · NRW-Schwerpunkt
            </div>

            <h1
              className="ss-h1 mt-5 text-white"
              data-testid="hero-headline"
            >
              Leerstand sichern.<br />
              <span className="text-[var(--ss-gold)]">Verkauf beschleunigen.</span>
            </h1>

            <p
              className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              <strong className="text-white">SecurityStaging.de</strong> installiert vollständige, professionelle Funk-Alarmanlagen in leerstehenden Immobilien –{" "}
              <span className="text-white">spurlos</span>,{" "}
              <span className="text-white">in unter einer Stunde</span> und als Premium-Verkaufsargument bei jeder Besichtigung.
            </p>

            <ul className="mt-7 space-y-3 max-w-xl" data-testid="hero-bullets">
              {[
                { icon: ShieldCheck, text: "Schutz vor Einbruch, Vandalismus, Wasser & Brand" },
                { icon: Zap, text: "Moderne Funk-Technik – keine Kabel, keine Wandschäden" },
                { icon: MapPin, text: "Schwerpunkt NRW – Dortmund, Köln, Düsseldorf, Essen & Ruhrgebiet. Bundesweit verfügbar." },
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--ss-gold)]/15 text-[var(--ss-gold)]">
                    <b.icon className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                  <span className="text-slate-200">{b.text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href={`tel:${PHONE.tel}`}
                className="ss-btn-primary"
                data-testid="hero-phone-cta"
              >
                <Phone className="h-4 w-4" strokeWidth={2.5} />
                Jetzt anrufen · {PHONE.display}
              </a>
              <button
                onClick={() => document.getElementById("process")?.scrollIntoView({ behavior: "smooth" })}
                className="ss-btn-ghost"
                data-testid="hero-secondary-cta"
              >
                <PlayCircle className="h-4 w-4" />
                Ablauf ansehen
              </button>
            </div>

            <p className="mt-5 text-xs text-slate-400">
              {PHONE.hours} · Unverbindliche Beratung
            </p>
          </div>

          {/* Right: video */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.45)] bg-[#0A2340]">
              <div className="aspect-[16/10] w-full">
                <video
                  data-testid="hero-video"
                  className="h-full w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster={HERO_POSTER}
                  aria-label="Funk-Alarmanlage Installation – Video"
                >
                  <source src={HERO_VIDEO_URL} type="video/mp4" />
                </video>
              </div>

              {/* Floating badge */}
              <div className="absolute left-4 bottom-4 sm:left-5 sm:bottom-5 flex items-center gap-2 rounded-xl bg-[#071A2B]/85 backdrop-blur px-3 py-2 border border-white/10">
                <span className="inline-flex h-2 w-2 rounded-full bg-[var(--ss-gold)] shadow-[0_0_8px_var(--ss-gold)] animate-pulse" />
                <span className="text-xs font-semibold">Alarm-System · aktiv</span>
              </div>
            </div>

            {/* Stat card overlay */}
            <div className="absolute -bottom-6 -right-3 sm:-right-6 hidden sm:block">
              <div className="rounded-2xl bg-white text-[#071A2B] px-5 py-4 shadow-[0_20px_50px_rgba(0,0,0,0.35)] border border-slate-200">
                <div className="font-display text-2xl font-bold leading-none">&lt; 1 Std.</div>
                <div className="text-xs text-slate-600 mt-1">Installation vor Ort</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom marquee-style trust bar */}
      <div className="relative border-t border-white/10 bg-black/20">
        <div className="ss-container py-4 flex flex-wrap items-center justify-between gap-4 text-xs sm:text-sm text-slate-300">
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--ss-gold)]" /> Zertifizierte Premium-Sicherheitstechnik</span>
          <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-[var(--ss-gold)]" /> Spurlose Funk-Installation</span>
          <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[var(--ss-gold)]" /> Schwerpunkt NRW · Bundesweit im Einsatz</span>
        </div>
      </div>
    </section>
  );
};
